import { BalanceData, ChallengeKind, EnemyArchetype, LevelChallenge, STAR_CHALLENGE, STAR_HP, STAR_WIN, StarMask } from "@highmarch/domain";
import { LEVELS_PER_CHAPTER } from "@highmarch/app";
import { icon } from "../iconsKit";

/** What the battle card (and the campaign map) says about a level, from the level data alone. */
export interface LevelFacts {
  readonly level: number;
  readonly name: string;
  readonly stages: number;
  /** The chapter's last level: its boss walks out at the end (campaign map rule). */
  readonly isBossLevel: boolean;
  readonly boss: EnemyArchetype;
  /** Unique enemy types in spawn order. */
  readonly enemies: readonly EnemyArchetype[];
  /** Enemy types that no earlier level sends. */
  readonly newEnemies: readonly EnemyArchetype[];
}

const seenBefore = new WeakMap<BalanceData, Set<number>[]>();

/** Enemy indices sent by any level below `level` (memoised per balance: a running union). */
function enemiesBefore(B: BalanceData, level: number): ReadonlySet<number> {
  let table = seenBefore.get(B);
  if (!table) {
    table = [new Set<number>()];
    const acc = new Set<number>();
    for (let l = 1; l <= B.levels.length; l++) {
      table.push(new Set(acc));                            // table[l] = everything in levels 1..l−1
      for (const g of B.level(l).groups) acc.add(g.enemyIndex);
    }
    seenBefore.set(B, table);
  }
  return table[Math.min(level, table.length - 1)];
}

export function levelFacts(B: BalanceData, level: number): LevelFacts {
  const L = B.level(level);
  const ids = [...new Set(L.groups.map((g) => g.enemyIndex))];
  const before = enemiesBefore(B, level);
  const enemies = ids.map((i) => B.enemies[i]);
  return {
    level, name: L.name, stages: B.stagesOf(level),
    isBossLevel: level % LEVELS_PER_CHAPTER === 0 || level === B.levels.length,
    boss: B.enemies[L.bossEnemyIndex],
    enemies, newEnemies: level > 1 ? enemies.filter((e) => !before.has(e.index)) : [],
  };
}

/** The sub-line under the level name: "20 stages · first flyers", "Boss · Plague Lord · 25 stages", "First battle". */
export function levelSubline(f: LevelFacts): string {
  if (f.level === 1) return `${f.stages} waves · your first battle`;
  if (f.isBossLevel) return `Boss · ${f.boss.name} · ${f.stages} waves`;
  const air = f.newEnemies.find((e) => e.isAir);
  const hint = air ? "first flyers" : f.newEnemies[0] ? `new: ${f.newEnemies[0].name}` : "";
  return `${f.stages} waves${hint ? ` · ${hint}` : ""}`;
}

/** A MinHp challenge at this percent reads "No damage". */
const FULL_HP_PERCENT = 100;
/** Star glyph size in the compact condition row (battle card, campaign panel). */
const ROW_STAR_PX = 12;

/** The third star's condition in a few words (battle card, campaign panel, end screen). */
export function challengeText(B: BalanceData, c: LevelChallenge | null): string {
  if (c === null) return "No challenge";
  switch (c.kind) {
    case ChallengeKind.MaxTowers: return `Max ${c.n} towers`;
    case ChallengeKind.NoTower: return `No ${B.towers[c.n].name}`;
    case ChallengeKind.MinHp: return c.n >= FULL_HP_PERCENT ? "No damage" : `Keep ${c.n}% HP`;
    case ChallengeKind.MaxUpgrades: return c.n === 0 ? "No upgrades" : `Max ${c.n} upgrades`;
    case ChallengeKind.NoReroll: return "No rerolls";
    case ChallengeKind.MaxRoad: return `Max ${c.n} road cards`;
    case ChallengeKind.NoSkip: return "Never skip";
    case ChallengeKind.EarlyCalls: return `Call early ×${c.n}`;
    default: return "";
  }
}

/** The level's three star conditions in star order, each with its StarMask bit. */
export function starConditions(B: BalanceData, level: number): readonly { readonly bit: number; readonly text: string }[] {
  return [
    { bit: STAR_WIN, text: "Win" },
    { bit: STAR_HP, text: `Keep ${B.level(level).starHpPercent}% HP` },
    { bit: STAR_CHALLENGE, text: challengeText(B, B.level(level).challenge) },
  ];
}

/** The compact condition row "★ Win · ★ 75% HP · ★ No Fire", the conditions in `earned` filled. */
export function starRowHtml(B: BalanceData, level: number, earned: StarMask): string {
  const filled = icon("star", ROW_STAR_PX, undefined, 2).replace('fill="none"', 'fill="currentColor"');
  const empty = icon("star", ROW_STAR_PX, undefined, 2);
  return `<span class="starrow">${starConditions(B, level).map((c) => `<span class="sc${earned & c.bit ? " on" : ""}">${earned & c.bit ? filled : empty}<i>${c.text}</i></span>`).join("")}</span>`;
}
